/**
 * PaymentsSection - Payment method configuration
 *
 * Uses shared components:
 * - SingleOptionFilter (composed) - view selector
 * - MenuButton (composed) - navigation
 * - PaymentMethodController (composed) - method settings
 * - PaymentMethodPillList (composed) - cascade ordering
 *
 * Views:
 * - Deposit Methods: toggle active/inactive, set min/max
 * - Withdrawal Methods: toggle active/inactive, set min/max
 * - Deposit Cascade: drag to reorder priority
 * - Withdrawal Cascade: drag to reorder priority
 */

import { useState } from 'react';
import {
  Settings,
  LayoutDashboard,
  Users,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Gift,
} from 'lucide-react';
import {
  SingleOptionFilter,
  MenuButton,
  PaymentMethodController,
  PaymentMethodControllerList,
  PaymentMethodPillList,
} from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { PaymentMethodItem } from '@/components/shared/composed/PaymentMethodPill';

// ============================================
// TYPES
// ============================================

export type PaymentView = 'deposit_methods' | 'withdrawal_methods' | 'deposit_cascade' | 'withdrawal_cascade';

export interface PaymentMethod {
  id: string;
  name: string;
  isActive: boolean;
  minAmount: number;
  maxAmount: number;
}

export interface PaymentsSectionProps {
  /** Deposit methods configuration */
  depositMethods: PaymentMethod[];
  /** Withdrawal methods configuration */
  withdrawalMethods: PaymentMethod[];
  /** Deposit cascade order */
  depositCascade: PaymentMethodItem[];
  /** Withdrawal cascade order */
  withdrawalCascade: PaymentMethodItem[];
  /** Update deposit method */
  onUpdateDepositMethod?: (id: string, updates: Partial<PaymentMethod>) => void;
  /** Update withdrawal method */
  onUpdateWithdrawalMethod?: (id: string, updates: Partial<PaymentMethod>) => void;
  /** Reorder deposit cascade */
  onReorderDepositCascade?: (items: PaymentMethodItem[]) => void;
  /** Reorder withdrawal cascade */
  onReorderWithdrawalCascade?: (items: PaymentMethodItem[]) => void;
  /** Navigate to other admin pages */
  onNavigate?: (page: string) => void;
  /** Loading state */
  isLoading?: boolean;
}

// View options
const viewOptions = [
  { value: 'deposit_methods', label: 'Deposit Methods' },
  { value: 'withdrawal_methods', label: 'Withdrawal Methods' },
  { value: 'deposit_cascade', label: 'Deposit Cascade' },
  { value: 'withdrawal_cascade', label: 'Withdrawal Cascade' },
];

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'payments', label: 'Payments', icon: Settings },
  { value: 'referees', label: 'Referees', icon: Users },
];

// ============================================
// COMPONENT
// ============================================

export function PaymentsSection({
  depositMethods,
  withdrawalMethods,
  depositCascade,
  withdrawalCascade,
  onUpdateDepositMethod,
  onUpdateWithdrawalMethod,
  onReorderDepositCascade,
  onReorderWithdrawalCascade,
  onNavigate,
  isLoading = false,
}: PaymentsSectionProps) {
  const [view, setView] = useState<PaymentView>('deposit_methods');

  const handleViewChange = (value: string) => {
    setView(value as PaymentView);
  };

  // Get title based on view
  const getTitle = () => {
    switch (view) {
      case 'deposit_methods':
        return 'Deposit Methods';
      case 'withdrawal_methods':
        return 'Withdrawal Methods';
      case 'deposit_cascade':
        return 'Deposit Cascade';
      case 'withdrawal_cascade':
        return 'Withdrawal Cascade';
    }
  };

  // Get description based on view
  const getDescription = () => {
    switch (view) {
      case 'deposit_methods':
        return 'Configure deposit method limits and status';
      case 'withdrawal_methods':
        return 'Configure withdrawal method limits and status';
      case 'deposit_cascade':
        return 'Drag to set deposit method priority';
      case 'withdrawal_cascade':
        return 'Drag to set withdrawal method priority';
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Header with menu */}
      <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <SingleOptionFilter
            options={viewOptions}
            value={view}
            onChange={handleViewChange}
            sheetTitle="Select View"
            showAllOption={false}
          />
          <MenuButton
            options={adminMenuOptions}
            value="payments"
            onSelect={(page) => onNavigate?.(page)}
            sheetTitle="Navigate"
          />
        </div>
      </div>

      {/* Title */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2">
        <h1 className="text-xl font-bold text-slate-800">{getTitle()}</h1>
        <p className="text-sm text-slate-500">{getDescription()}</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-24 rounded-2xl bg-white animate-pulse"
              />
            ))}
          </div>
        ) : (
          <>
            {/* Deposit Methods View */}
            {view === 'deposit_methods' && (
              <PaymentMethodControllerList>
                {depositMethods.map((method) => (
                  <PaymentMethodController
                    key={method.id}
                    name={method.name}
                    isActive={method.isActive}
                    onToggle={(active) =>
                      onUpdateDepositMethod?.(method.id, { isActive: active })
                    }
                    minAmount={method.minAmount}
                    maxAmount={method.maxAmount}
                    onMinChange={(value) =>
                      onUpdateDepositMethod?.(method.id, {
                        minAmount: Number(value) || 0,
                      })
                    }
                    onMaxChange={(value) =>
                      onUpdateDepositMethod?.(method.id, {
                        maxAmount: Number(value) || 0,
                      })
                    }
                  />
                ))}
              </PaymentMethodControllerList>
            )}

            {/* Withdrawal Methods View */}
            {view === 'withdrawal_methods' && (
              <PaymentMethodControllerList>
                {withdrawalMethods.map((method) => (
                  <PaymentMethodController
                    key={method.id}
                    name={method.name}
                    isActive={method.isActive}
                    onToggle={(active) =>
                      onUpdateWithdrawalMethod?.(method.id, { isActive: active })
                    }
                    minAmount={method.minAmount}
                    maxAmount={method.maxAmount}
                    onMinChange={(value) =>
                      onUpdateWithdrawalMethod?.(method.id, {
                        minAmount: Number(value) || 0,
                      })
                    }
                    onMaxChange={(value) =>
                      onUpdateWithdrawalMethod?.(method.id, {
                        maxAmount: Number(value) || 0,
                      })
                    }
                  />
                ))}
              </PaymentMethodControllerList>
            )}

            {/* Deposit Cascade View */}
            {view === 'deposit_cascade' && (
              <PaymentMethodPillList
                items={depositCascade}
                onReorder={(items) => onReorderDepositCascade?.(items)}
              />
            )}

            {/* Withdrawal Cascade View */}
            {view === 'withdrawal_cascade' && (
              <PaymentMethodPillList
                items={withdrawalCascade}
                onReorder={(items) => onReorderWithdrawalCascade?.(items)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
